/**
 * Bannière de statut réseau affichée quand la connexion est dégradée
 */

import { useEffect, useState } from 'react';
import { WifiOff, Wifi, AlertTriangle, X } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface NetworkStatusBannerProps {
  className?: string;
}

type NetworkStatus = 'online' | 'offline' | 'slow';

export function NetworkStatusBanner({ className }: NetworkStatusBannerProps) {
  const [status, setStatus] = useState<NetworkStatus>('online');
  const [dismissed, setDismissed] = useState(false);
  const [responseTime, setResponseTime] = useState<number | null>(null);

  useEffect(() => {
    const handleOnline = () => {
      setStatus('online');
      setDismissed(false);
    };

    const handleOffline = () => {
      setStatus('offline');
      setDismissed(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Vérification initiale
    if (!navigator.onLine) {
      setStatus('offline');
    }

    // Vérification périodique de la latence
    const checkLatency = async () => {
      if (!navigator.onLine) return;
      
      const start = performance.now();
      try {
        await fetch('/favicon.ico', { 
          method: 'HEAD',
          cache: 'no-cache'
        });
        const latency = performance.now() - start;
        setResponseTime(latency);
        
        if (latency > 3000) {
          setStatus('slow');
          setDismissed(false);
        } else if (status === 'slow') {
          setStatus('online');
        }
      } catch {
        // Ignore fetch errors - handled by online/offline events
      }
    };

    const interval = setInterval(checkLatency, 30000);
    checkLatency();

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(interval);
    };
  }, [status]);

  // Ne rien afficher si online et non dismissed
  if (status === 'online' || dismissed) {
    return null;
  }

  const config = {
    offline: {
      icon: WifiOff,
      title: 'Mode hors ligne',
      description: 'Vous êtes actuellement hors ligne. Certaines fonctionnalités sont indisponibles.',
      variant: 'destructive' as const,
      bgClass: 'bg-destructive/10 border-destructive/30'
    },
    slow: {
      icon: AlertTriangle,
      title: 'Connexion lente',
      description: `Connexion lente détectée (${Math.round(responseTime || 0)}ms). Les performances peuvent être réduites.`,
      variant: 'default' as const,
      bgClass: 'bg-warning/10 border-warning/30'
    }
  };

  const { icon: Icon, title, description, bgClass } = config[status];

  return (
    <Alert 
      className={cn(
        'fixed top-0 left-0 right-0 z-50 rounded-none border-x-0 border-t-0',
        bgClass,
        className
      )}
    >
      <div className="container flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Icon className="h-5 w-5" />
          <div>
            <span className="font-medium">{title}</span>
            <AlertDescription className="text-sm">
              {description}
            </AlertDescription>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {status === 'offline' && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.location.reload()}
              className="gap-2"
            >
              <Wifi className="h-4 w-4" />
              Réessayer
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setDismissed(true)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </Alert>
  );
}
